import { useEffect, useState } from "react";
import { isCounterBooking, sttRows } from "./stt";
import {
  DOCTOR_REQUIRED_HINT,
  DOCTOR_REQUIRED_MESSAGE,
  ROOM_REQUIRED_HINT,
  ROOM_REQUIRED_MESSAGE,
  hasDoctor,
  hasRoom,
  startEncounterErrorMessage,
} from "../../shared/encounterStart";
import { useRoomRequired } from "../../shared/hooks/useRoomRequired";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Cake,
  CalendarClock,
  CalendarDays,
  IdCard,
  Mail,
  MapPin,
  Pencil,
  Phone,
  QrCode,
  Send,
  User,
  VenusAndMars,
  X,
  type LucideIcon,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  appointmentQueryKeys,
  appointmentsApi,
  editAppointment,
} from "../../shared/api/appointments";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import { patientQueryKeys, patientsApi } from "../../shared/api/patients";
import { useGeoNames } from "../../shared/api/geo";
import { roomsApi, roomQueryKeys, assignRoom } from "../../shared/api/rooms";
import { meApi, meQueryKeys } from "../../shared/api/me";
import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../shared/api/hisIntegration";
import { feedbackApi } from "../../shared/api/feedback";
import { apiError } from "../../shared/api/client";
import { SlotConflictHint, isSlotConflictError } from "../../shared/ui/SlotConflictHint";
import {
  formatDate,
  formatDateTime,
  formatPhone,
  formatTime,
  formatVndWithUnit,
  formatWeekday,
  genderLabel,
} from "../../shared/format";
import type { AppointmentDetail, AppointmentStatus } from "../../shared/types/appointment";
import { PageHeader } from "../../shared/ui/PageHeader";
import { PatientLink } from "../../shared/ui/PatientLink";
import { DepartmentSelect } from "../../shared/ui/DepartmentSelect";
import { ErrorState } from "../../shared/ui/ErrorState";
import { Skeleton } from "../../shared/ui/Skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AppointmentStatusBadge } from "./components/AppointmentStatusBadge";
import { AppointmentProgress } from "./components/AppointmentProgress";
import { HisSyncStatusBadge, type HisSyncStatus } from "./components/HisSyncStatusBadge";
import { ExamFeeCard } from "./components/ExamFeeCard";
import { EncounterResultCard } from "./components/EncounterResultCard";
import { PatientAvatar } from "../patients/components/PatientAvatar";
import { isCheckInDay } from "./checkInDay";
import { useOperatesModule } from "../../shared/hooks/useOperatesModule";
import { TimeInput } from "../../shared/ui/TimeInput";

/** Native <select>/<textarea> styled to match the shadcn Input (h-9, border-input). */
const NATIVE_CONTROL =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const NATIVE_TEXTAREA =
  "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

/** Map the workflow-action variant to a shadcn Button variant. */
const BTN_VARIANT: Record<
  "primary" | "secondary" | "danger",
  "default" | "outline" | "destructive"
> = {
  primary: "default",
  secondary: "outline",
  danger: "destructive",
};

/** Workflow buttons available given the current status. */
function nextActions(status: AppointmentStatus): {
  label: string;
  to: AppointmentStatus;
  variant: "primary" | "secondary" | "danger";
}[] {
  switch (status) {
    case "REQUESTED":
      return [
        { label: "Xác nhận", to: "CONFIRMED", variant: "primary" },
        { label: "Hủy", to: "CANCELLED", variant: "danger" },
      ];
    case "CONFIRMED":
      return [
        { label: "Nhận khám (check-in)", to: "CHECKED_IN", variant: "primary" },
        { label: "Bệnh nhân không đến", to: "NO_SHOW", variant: "secondary" },
        { label: "Hủy", to: "CANCELLED", variant: "danger" },
      ];
    case "CHECKED_IN":
      // Note: the primary action ("Bắt đầu khám") creates an encounter +
      // navigates — handled outside this list because it isn't a status
      // transition. "Hoàn thành (không khám)" is the rare path when the
      // patient was checked in but doesn't actually need an encounter.
      return [
        { label: "Hoàn thành (không khám)", to: "COMPLETED", variant: "secondary" },
      ];
    default:
      return [];
  }
}

/** Split an ISO instant into the date + time strings the reschedule/edit inputs expect. */
function splitSlot(iso: string): { date: string; time: string } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/**
 * Dòng dịch vụ trên thẻ "Dịch vụ & Thanh toán". `priceVnd` null = chưa niêm yết ("Liên hệ", #19237);
 * `legacy` = máy chủ cũ chỉ trả `serviceName`, không biết gì về giá — hiện "—" chứ không phải
 * "Liên hệ", vì hai thứ đó là hai câu khác nhau với bệnh nhân.
 *
 * `shownVnd` là con số thật sự in ra: giá ĐÃ THU trên hoá đơn của lượt khám khi có
 * (`invoicedPriceVnd`), nếu không thì giá niêm yết — như SalePro in `unitPrice` của dòng trên phiếu
 * chứ không in giá sản phẩm. `invoiced` nói dòng này lấy từ hoá đơn, để dòng tổng ghi đúng nguồn.
 */
interface ServiceRow {
  key: string;
  name: string;
  priceVnd: number | null;
  shownVnd: number | null;
  invoiced: boolean;
  legacy: boolean;
}

/**
 * Danh sách dịch vụ để HIỆN (#19630): `services[]` khi máy chủ trả, nếu không thì một dòng từ
 * `serviceName` (hợp đồng cũ). Chỉ đổi cách hiện — phí khám, hoá đơn, mẫu tin nhắn và sức chứa
 * khung giờ vẫn đọc `serviceId` (dịch vụ chính) như quyết định ở #19408.
 */
export function serviceRows(a: Pick<AppointmentDetail, "services" | "serviceId" | "serviceName">): ServiceRow[] {
  if (a.services) {
    return a.services.map((s, i) => {
      const invoiced = s.invoicedPriceVnd != null;
      return {
        key: s.serviceId ?? String(i),
        name: s.serviceName,
        priceVnd: s.priceVnd,
        shownVnd: invoiced ? (s.invoicedPriceVnd as number) : s.priceVnd,
        invoiced,
        legacy: false,
      };
    });
  }
  if (a.serviceName) {
    return [
      {
        key: a.serviceId ?? "legacy",
        name: a.serviceName,
        priceVnd: null,
        shownVnd: null,
        invoiced: false,
        legacy: true,
      },
    ];
  }
  return [];
}

export function AppointmentDetailPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [rescheduling, setRescheduling] = useState(false);
  // #19167: lỗi của thao tác đổi trạng thái (vd. 422 check_in_wrong_day) — trước đây im lặng.
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [reschedReason, setReschedReason] = useState("");
  const [roomId, setRoomId] = useState("");
  const [roomMsg, setRoomMsg] = useState<string | null>(null);
  const [fbRating, setFbRating] = useState(0);
  const [fbComment, setFbComment] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  // "Sửa lịch hẹn" (Redmine #18507) — full-edit inline form.
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editDuration, setEditDuration] = useState(0);
  const [editDoctorId, setEditDoctorId] = useState<string | null>(null);
  const [editDepartmentId, setEditDepartmentId] = useState<string | null>(null);
  const [editServiceId, setEditServiceId] = useState<string | null>(null);
  const [editRoomId, setEditRoomId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState("");

  const detail = useQuery({
    queryKey: appointmentQueryKeys.detail(id),
    queryFn: () => appointmentsApi.get(id),
  });

  /**
   * #19201 — a refusal because the appointment changed elsewhere (409/422: another desk rescheduled
   * it, it was cancelled, a doctor was assigned…) must not leave the OLD date, STT and buttons on
   * screen: staff then call the wrong number or press a button that no longer applies. Reload the
   * appointment once, right after the error is shown — once per refusal, never a polling loop. Other
   * failures (network, 5xx) say nothing about the appointment's state, so they do not trigger it.
   */
  function refreshIfStale(e: unknown) {
    const status = apiError(e).status;
    if (status === 409 || status === 422) {
      // `all` is the prefix of `detail(id)`: one call refreshes both, and the page fetches once.
      void qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
    }
  }

  const transition = useMutation({
    mutationFn: ({ to, note }: { to: AppointmentStatus; note?: string }) =>
      appointmentsApi.transition(id, to, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
      // A CANCELLED transition flips outbound_booking to action=CANCEL/PENDING_SYNC in the same
      // request (#18676) — without this the "Thông tin HIS" panel keeps showing whatever state
      // the row was in before the cancel and never picks up the flip (its own refetchInterval
      // only re-polls while the currently-cached status is PENDING_SYNC).
      qc.invalidateQueries({ queryKey: hisIntegrationQueryKeys.bookingFor(id) });
      setTransitionError(null);
    },
    onError: (e) => {
      setTransitionError(apiError(e).detail);
      refreshIfStale(e);
    },
  });

  // "Bắt đầu khám" mở một lần khám trong Addy. Phòng khám không dùng vùng Khám bệnh — vì chế độ
  // vận hành (ADR-0048) hay vì gói (ADR-0053) — thì không có nút, như ở màn Tiếp đón.
  const { operates: operatesEncounters } = useOperatesModule("ENCOUNTERS");
  // #19665 — Phòng = Bắt buộc: no room, no NEW encounter ("Tiếp tục khám" stays available).
  const roomRequired = useRoomRequired();

  const startEncounter = useMutation({
    mutationFn: () => {
      const a = detail.data!;
      return encountersApi.createFromAppointment({
        appointmentId: a.id,
        patientId: a.patientId,
        patientName: a.patientName,
        patientCode: a.patientCode ?? null,
        patientPhone: a.patientPhone,
        patientGender: null,
        patientAge: null,
        doctorId: a.doctorId,
        doctorName: a.doctorName,
      });
    },
    onMutate: () => setTransitionError(null),
    onSuccess: (encounter) => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.detail(id) });
      navigate(`/encounters/${encounter.id}`);
    },
    // #18899 — there was no onError here, so a refused start did nothing at all on screen.
    onError: (e) => {
      setTransitionError(startEncounterErrorMessage(e));
      refreshIfStale(e);
    },
  });

  const reschedule = useMutation({
    mutationFn: () =>
      appointmentsApi.reschedule(id, {
        startAt: new Date(`${newDate}T${newTime}:00`).toISOString(),
        durationMinutes: detail.data!.durationMinutes,
        reason: reschedReason || null,
      }),
    onSuccess: () => {
      setRescheduling(false);
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
    },
    // Refresh the detail on a state refusal too — e.g. the appointment was cancelled elsewhere
    // (stale page) so the reschedule form should disappear once the status updates (#19201).
    onError: (e) => refreshIfStale(e),
  });

  const doctorOptions = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
    staleTime: 5 * 60 * 1000,
  });
  const serviceOptions = useQuery({
    queryKey: appointmentQueryKeys.services(),
    queryFn: () => appointmentsApi.listServices(),
    staleTime: 5 * 60 * 1000,
  });

  const edit = useMutation({
    mutationFn: () =>
      editAppointment(id, {
        startAt: new Date(`${editDate}T${editTime}:00`).toISOString(),
        durationMinutes: editDuration,
        doctorProfileId: editDoctorId,
        departmentId: editDepartmentId,
        serviceId: editServiceId,
        roomId: editRoomId,
        notes: editNotes.trim() ? editNotes.trim() : null,
      }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.detail(id) });
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
    },
    onError: (e) => refreshIfStale(e),
  });

  const rooms = useQuery({
    queryKey: roomQueryKeys.list(true),
    queryFn: () => roomsApi.list(true),
  });
  const me = useQuery({
    queryKey: meQueryKeys.profile(),
    queryFn: () => meApi.get(),
    staleTime: 5 * 60 * 1000,
  });

  /*
   * Hồ sơ bệnh nhân cho khối "Thông tin bệnh nhân" (giới tính, địa chỉ, email, ảnh) — như thẻ
   * `customerDTO` trên màn SalePro. `GET /v1/appointments/{id}` chỉ mang tên/SĐT/mã/ngày sinh, nên
   * đọc thêm hồ sơ; chưa về hoặc lỗi thì màn vẫn dựng đủ từ lịch hẹn, không chờ.
   */
  const patientId = detail.data?.patientId ?? "";
  const patient = useQuery({
    queryKey: patientQueryKeys.detail(patientId),
    queryFn: () => patientsApi.get(patientId),
    enabled: !!patientId,
    staleTime: 60 * 1000,
  });
  const { provinceName, wardName } = useGeoNames();

  /*
   * Thẻ "Kết quả khám" (SalePro: "Kết quả & Tài liệu"): lần khám mở từ lịch hẹn này. Chỉ hỏi khi
   * đã có khám (đang khám / đã khám xong) — lịch chưa tới quầy thì không có gì để đọc, và đây là
   * một lần đọc hồ sơ lâm sàng có kiểm toán (@PhiAccess) nên không gọi vô ích.
   */
  const hasExam = detail.data?.inExam === true || detail.data?.status === "COMPLETED";
  const encounters = useQuery({
    queryKey: encounterQueryKeys.list({ patientId, appointmentId: id }, 0),
    queryFn: () => encountersApi.list({ patientId }, { page: 0, size: 50 }),
    enabled: hasExam && !!patientId,
    select: (page) => page.content.find((e) => e.appointmentId === id) ?? null,
  });

  // Tenant HIS connection (ADR-0020): hisType !== "NATIVE" && enabled is the same check the
  // backend itself uses (HisSyncService.onBookingCreated, HisDeliveryService.runForTenant) to
  // decide whether a tenant's bookings get enqueued at all — reusing it here, not inventing a
  // second definition of "has HIS" on the frontend.
  const binding = useQuery({
    queryKey: hisIntegrationQueryKeys.binding(),
    queryFn: () => hisIntegrationApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const hasHis = binding.data ? binding.data.hisType !== "NATIVE" && binding.data.enabled : false;

  // The one outbound_booking row for this appointment, if any — "Thông tin HIS" panel below.
  // Refetches periodically while a sync is still in flight so staff watching the page see it
  // flip from "Đang xử lý" to "Đã gửi HIS thành công" without a manual reload.
  const hisBooking = useQuery({
    queryKey: hisIntegrationQueryKeys.bookingFor(id),
    queryFn: () => hisIntegrationApi.syncedBookings(undefined, id),
    enabled: hasHis && !!id,
    refetchInterval: (query) =>
      query.state.data?.[0]?.status === "PENDING_SYNC" ? 5000 : false,
  });
  // Manual "push this booking to the HIS now" — drains the tenant's delivery outbox on demand
  // instead of waiting for the immediate-after-commit attempt or the scheduler poll. Used from the
  // HIS panel below when a booking is still NOT_ENQUEUED / PENDING_SYNC / REJECTED / FAILED.
  const pushHis = useMutation({
    mutationFn: () => hisIntegrationApi.runSync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: hisIntegrationQueryKeys.bookingFor(id) }),
  });
  const callRoom = useMutation({
    mutationFn: () => assignRoom(id, roomId),
    onSuccess: (roomNo) => {
      const r = (rooms.data ?? []).find((x) => x.id === roomId);
      setTransitionError(null);
      setRoomMsg(`Đã gọi vào ${r?.name ?? "phòng"} — số thứ tự phòng: ${roomNo}`);
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.detail(id) });
    },
    /**
     * #19341 — the refusal goes to `transitionError`, not `roomMsg`, for two reasons that both
     * showed up as "the screen says nothing":
     *
     * 1. `roomMsg` renders in `text-emerald-600`, so a refusal was painted as a success.
     * 2. The whole "Gọi vào phòng" card only exists while the status is CHECKED_IN — and the
     *    refusal that matters here is exactly the one that reveals the status moved on. The
     *    reload below therefore unmounts the card, taking its message with it. `transitionError`
     *    is rendered by the status card in BOTH its live and terminal views (#19201), which
     *    is the one place a message survives the state it reported.
     */
    onError: (e) => {
      setRoomMsg(null);
      setTransitionError(apiError(e).detail);
      refreshIfStale(e);
    },
  });

  // Đánh giá sau khám — record a patient rating for a completed appointment.
  const submitFeedback = useMutation({
    mutationFn: () =>
      feedbackApi.submit({
        patientId: detail.data?.patientId as string,
        appointmentId: id,
        rating: fbRating,
        comment: fbComment || null,
        submittedVia: "STAFF",
      }),
  });

  // Pre-select the room already assigned via "Gọi vào phòng" so re-opening the appointment shows
  // the saved room instead of "— Chọn phòng —" (#18579). The detail response already carries
  // roomId; the dropdown state just wasn't initialised from it.
  useEffect(() => {
    const rid = detail.data?.roomId;
    if (rid) setRoomId(rid);
  }, [detail.data?.roomId]);

  // Guard AFTER hooks so hook order is stable (Rules of Hooks / React #310).
  if (!rawId) return <Navigate to="/appointments" replace />;

  if (detail.isLoading) {
    return (
      <div>
        <PageHeader title="Chi tiết đặt lịch" description="Đang tải lịch hẹn…" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="grid gap-4 lg:col-span-2">
            <Skeleton height={160} />
            <Skeleton height={240} />
          </div>
          <Skeleton height={320} />
        </div>
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div>
        <PageHeader title="Không tìm thấy lịch hẹn" />
        <ErrorState
          error={detail.error}
          onRetry={() => detail.refetch()}
        >
          <Button asChild variant="outline">
            <Link to="/appointments">
              <ArrowLeft aria-hidden="true" />
              Danh sách lịch hẹn
            </Link>
          </Button>
        </ErrorState>
      </div>
    );
  }

  const a = detail.data;
  const p = patient.data ?? null;
  const rows = serviceRows(a);
  const pricedRows = rows.filter((r) => r.shownVnd != null);
  const hasUnpriced = rows.some((r) => !r.legacy && r.shownVnd == null);
  const total = pricedRows.reduce((sum, r) => sum + (r.shownVnd ?? 0), 0);
  // Dòng tổng nói rõ nguồn: có ít nhất một dòng lấy từ hoá đơn thì là "theo hoá đơn DH…", không
  // phải "giá niêm yết" — hai con số có thể khác nhau, và người đọc phải biết đang nhìn cái nào.
  const invoiceOfTotal = rows.some((r) => r.invoiced) ? (a.linkedInvoice ?? null) : null;
  const actions = nextActions(a.status);
  const cancelAction = actions.find((act) => act.to === "CANCELLED") ?? null;
  const inlineActions = actions.filter((act) => act.to !== "CANCELLED");
  const isTerminal =
    a.status === "COMPLETED" ||
    a.status === "CANCELLED" ||
    a.status === "NO_SHOW";

  // Địa chỉ: số nhà/đường + phường + tỉnh, tên tra từ Geo; mã không tra được thì giữ mã như
  // OverviewPanel, hơn là lặng lẽ bỏ qua một phần địa chỉ. Dữ liệu migrate từ SalePro hay mang
  // sẵn dấu phẩy cuối ("…, Tỉnh Bắc Ninh,"), nên cắt dấu phân cách thừa ở mỗi phần trước khi nối.
  const geo = (code: string | null | undefined, names: Map<string, string>): string | null =>
    code ? (names.get(String(code)) ?? String(code)) : null;
  const address = p
    ? [p.addressLine, geo(p.wardCode, wardName), geo(p.provinceCode, provinceName)]
        .map((part) => part?.replace(/^[\s,;]+|[\s,;]+$/g, "") ?? "")
        .filter(Boolean)
        .join(", ")
    : "";
  const phone = p?.phoneE164 ?? a.patientPhone;
  const dob = p?.dateOfBirth ?? a.patientDateOfBirth;

  // outbound_booking row for this appointment (0 or 1 — appointment_id is unique per tenant on
  // that table). No row yet is a real, common state (enqueue race right after creation, or the
  // hybrid immediate-delivery attempt hasn't run) — NOT_ENQUEUED, not an error.
  const hisRow = hisBooking.data?.[0] ?? null;
  const hisSyncStatus: HisSyncStatus = (hisRow?.status as HisSyncStatus | undefined) ?? "NOT_ENQUEUED";

  // Manual push button visibility (owner decision, 2026-08-23):
  //  - only in MANUAL delivery mode — in AUTO the background scheduler pushes every booking with no
  //    button at all (delivery is fully decoupled from the web);
  //  - only while the appointment is newly-created (REQUESTED/CONFIRMED, i.e. not yet checked in) —
  //    that's the window to reserve the slot/STT on the HIS, before the patient arrives.
  const deliveryMode = binding.data?.deliveryMode ?? "AUTO";
  const isNewlyCreated = a.status === "REQUESTED" || a.status === "CONFIRMED";
  const canManualPush = hasHis && deliveryMode === "MANUAL" && isNewlyCreated;

  function askCancelReason() {
    const reason = prompt("Lý do hủy lịch hẹn?", "Bệnh nhân yêu cầu");
    if (reason !== null && reason.trim()) {
      transition.mutate({ to: "CANCELLED", note: reason });
    }
  }

  function openReschedule() {
    const slot = splitSlot(a.startAt);
    if (slot) {
      setNewDate(slot.date);
      setNewTime(slot.time);
    }
    setRescheduling(true);
  }

  function openEdit() {
    const slot = splitSlot(a.startAt);
    if (slot) {
      setEditDate(slot.date);
      setEditTime(slot.time);
    }
    setEditDuration(a.durationMinutes);
    setEditDoctorId(a.doctorId || null);
    setEditDepartmentId(a.departmentId ?? null);
    setEditServiceId(a.serviceId ?? null);
    setEditRoomId(a.roomId ?? null);
    setEditNotes(a.notes ?? "");
    setEditing(true);
  }

  return (
    <div>
      {/*
       * Bố cục theo màn "Chi tiết đặt lịch" của SalePro: nút Chỉnh sửa / Hủy lịch ở đầu trang,
       * cột trái 2/3 (thời gian + tiến trình → thông tin bệnh nhân → dịch vụ & thanh toán → kết
       * quả → lịch sử), cột phải 1/3 (QR + mã đặt lịch / STT / phòng, rồi HIS và đánh giá).
       */}
      <PageHeader
        title="Chi tiết đặt lịch"
        description={
          <>
            {a.patientName}
            {a.patientCode && <> · {a.patientCode}</>}
            {" — "}
            {formatDate(a.startAt)} lúc <strong>{formatTime(a.startAt)}</strong>
          </>
        }
        breadcrumbs={[
          { label: "Trang chủ", to: "/dashboard" },
          { label: "Lịch hẹn", to: "/appointments" },
          { label: "Chi tiết đặt lịch" },
        ]}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/appointments">
                <ArrowLeft aria-hidden="true" />
                Danh sách
              </Link>
            </Button>
            {!isTerminal && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openReschedule}
                  disabled={rescheduling}
                >
                  <CalendarClock aria-hidden="true" />
                  Dời lịch
                </Button>
                <Button type="button" variant="outline" onClick={openEdit} disabled={editing}>
                  <Pencil aria-hidden="true" />
                  Chỉnh sửa
                </Button>
                {cancelAction && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={askCancelReason}
                    disabled={transition.isPending}
                  >
                    <X aria-hidden="true" />
                    Hủy lịch
                  </Button>
                )}
              </>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <div className="grid content-start gap-4 lg:col-span-2">
          {/* ── Thời gian · trạng thái · tiến trình · thao tác ─────────────────────────── */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span
                    className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
                    aria-hidden="true"
                  >
                    <CalendarDays className="size-6" />
                  </span>
                  <div>
                    <p className="m-0 text-2xl font-bold leading-tight tabular-nums text-foreground">
                      {formatTime(a.startAt)}
                    </p>
                    <p className="m-0 text-sm text-muted-foreground">
                      {formatWeekday(a.startAt)}, {formatDate(a.startAt)} · {a.durationMinutes} phút
                      {a.doctorName && (
                        <>
                          {" "}
                          · <span className="text-foreground">{a.doctorName}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-sm [&>span]:px-3 [&>span]:py-1 [&>span]:text-sm">
                  <AppointmentStatusBadge status={a.status} inExam={a.inExam} />
                </div>
              </div>

              <AppointmentProgress
                className="mt-5"
                status={a.status}
                inExam={a.inExam}
                events={a.events}
              />

              <Separator className="my-4" />

              {isTerminal ? (
                <>
                  <p className="m-0 text-sm text-muted-foreground">
                    Lịch hẹn đã ở trạng thái kết thúc.
                  </p>
                  {/* #19201 — the refusal that revealed the new state must stay readable after the
                      reload flips this card to the terminal view; otherwise the page changes with
                      no word as to why. */}
                  {transitionError && (
                    <p className="mt-2 text-sm text-destructive" role="alert">
                      {transitionError}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {a.status === "CHECKED_IN" && operatesEncounters && (
                    <Button
                      type="button"
                      onClick={() => startEncounter.mutate()}
                      // #18899 — an encounter takes its doctor from the appointment; without one
                      // there is nothing to start. Say why, next to the button (below).
                      disabled={
                        startEncounter.isPending ||
                        !hasDoctor(a) ||
                        (roomRequired && !hasRoom(a) && !a.inExam)
                      }
                      title={
                        !hasDoctor(a)
                          ? DOCTOR_REQUIRED_HINT
                          : roomRequired && !hasRoom(a) && !a.inExam
                            ? ROOM_REQUIRED_HINT
                            : undefined
                      }
                    >
                      {startEncounter.isPending
                        ? "Đang mở…"
                        : a.inExam
                          ? "Tiếp tục khám →"
                          : "Bắt đầu khám →"}
                    </Button>
                  )}
                  {inlineActions.map((act) => (
                    <Button
                      key={act.to}
                      type="button"
                      variant={BTN_VARIANT[act.variant]}
                      onClick={() => transition.mutate({ to: act.to })}
                      disabled={
                        transition.isPending ||
                        (act.to === "CHECKED_IN" && !isCheckInDay(a.startAt))
                      }
                      title={
                        act.to === "CHECKED_IN" && !isCheckInDay(a.startAt)
                          ? "Lịch hẹn không thuộc hôm nay — dời lịch sang hôm nay để nhận khám"
                          : undefined
                      }
                    >
                      {act.label}
                    </Button>
                  ))}
                  {inlineActions.length === 0 && !(a.status === "CHECKED_IN" && operatesEncounters) && (
                    <p className="m-0 text-sm text-muted-foreground">
                      Không có thao tác tiếp theo ở trạng thái này.
                    </p>
                  )}
                  {/* #19167: lý do phải NHÌN THẤY được, không chỉ nằm trong `title` (tablet không rê chuột). */}
                  {actions.some((act) => act.to === "CHECKED_IN") && !isCheckInDay(a.startAt) && (
                    <p className="basis-full text-sm text-amber-700">
                      Lịch hẹn không thuộc hôm nay — dời lịch sang hôm nay để nhận khám.
                    </p>
                  )}
                  {a.status === "CHECKED_IN" && !hasDoctor(a) && (
                    <p className="basis-full text-sm text-amber-700" data-testid="needs-doctor-hint">
                      {DOCTOR_REQUIRED_MESSAGE}
                    </p>
                  )}
                  {a.status === "CHECKED_IN" &&
                    operatesEncounters &&
                    hasDoctor(a) &&
                    roomRequired &&
                    !hasRoom(a) &&
                    !a.inExam && (
                      <p
                        className="basis-full text-sm text-amber-700"
                        data-testid="needs-room-hint"
                      >
                        {ROOM_REQUIRED_MESSAGE}
                      </p>
                    )}
                  {transitionError && (
                    <p className="basis-full text-sm text-destructive" role="alert">
                      {transitionError}
                    </p>
                  )}
                </div>
              )}

              {rescheduling && (
                <div className="mt-4 rounded-md bg-muted p-3.5">
                  <h3 className="mt-0 text-sm font-semibold">Dời lịch sang</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="rd">Ngày mới</Label>
                      <Input
                        id="rd"
                        type="date"
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rt">Giờ mới</Label>
                      <TimeInput id="rt" value={newTime} onChange={setNewTime} />
                    </div>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Label htmlFor="rr">Lý do</Label>
                    <Input
                      id="rr"
                      type="text"
                      value={reschedReason}
                      onChange={(e) => setReschedReason(e.target.value)}
                      placeholder="VD: bác sĩ có cuộc họp đột xuất"
                    />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setRescheduling(false)}
                    >
                      Hủy
                    </Button>
                    <Button
                      type="button"
                      onClick={() => reschedule.mutate()}
                      disabled={!newDate || !newTime || reschedule.isPending}
                    >
                      {reschedule.isPending ? "Đang lưu…" : "Xác nhận dời"}
                    </Button>
                  </div>
                  {reschedule.isError && (
                    // A taken slot is information, not a red error — same as the create page
                    // (#18787); #18681 measured hundreds of these in one data-entry session.
                    <div
                      className={
                        isSlotConflictError(reschedule.error)
                          ? "mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                          : "mt-2 text-sm text-destructive"
                      }
                      role={isSlotConflictError(reschedule.error) ? "status" : "alert"}
                    >
                      <p>{apiError(reschedule.error).detail}</p>
                      {/* Offer the doctor's next free starts rather than making the user
                          guess another time and press again (#18672). */}
                      <SlotConflictHint
                        error={reschedule.error}
                        onPick={(iso) => {
                          const slot = splitSlot(iso);
                          if (!slot) return;
                          setNewDate(slot.date);
                          setNewTime(slot.time);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
              {editing && (
                <div className="mt-4 rounded-md bg-muted p-3.5">
                  <h3 className="mt-0 text-sm font-semibold">Sửa thông tin lịch</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-date">Ngày</Label>
                      <Input
                        id="ed-date"
                        type="date"
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-time">Giờ</Label>
                      <TimeInput id="ed-time" value={editTime} onChange={setEditTime} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ed-duration">Thời lượng (phút)</Label>
                      <Input
                        id="ed-duration"
                        type="number"
                        min={1}
                        value={editDuration}
                        onChange={(e) => setEditDuration(Number(e.target.value))}
                      />
                    </div>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Label htmlFor="ed-doctor">Bác sĩ</Label>
                    <select
                      id="ed-doctor"
                      className={NATIVE_CONTROL}
                      value={editDoctorId ?? ""}
                      onChange={(e) => setEditDoctorId(e.target.value || null)}
                    >
                      <option value="">— Bác sĩ bất kỳ —</option>
                      {(doctorOptions.data ?? []).map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                          {d.specialty ? ` · ${d.specialty}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Label htmlFor="ed-department">Khoa</Label>
                    <DepartmentSelect
                      id="ed-department"
                      value={editDepartmentId}
                      onChange={setEditDepartmentId}
                    />
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Label htmlFor="ed-service">Dịch vụ</Label>
                    <select
                      id="ed-service"
                      className={NATIVE_CONTROL}
                      value={editServiceId ?? ""}
                      onChange={(e) => setEditServiceId(e.target.value || null)}
                    >
                      <option value="">— Không —</option>
                      {(serviceOptions.data ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Label htmlFor="ed-room">Phòng khám</Label>
                    <select
                      id="ed-room"
                      className={NATIVE_CONTROL}
                      value={editRoomId ?? ""}
                      onChange={(e) => setEditRoomId(e.target.value || null)}
                    >
                      <option value="">— Không —</option>
                      {(rooms.data ?? []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.code} · {r.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Đổi phòng sẽ cấp số thứ tự phòng mới (giữ nguyên số của phòng
                      cũ). Có thể đổi khoa/bác sĩ nếu bác sĩ khám ở phòng khác.
                    </p>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <Label htmlFor="ed-notes">Ghi chú</Label>
                    <textarea
                      id="ed-notes"
                      className={NATIVE_TEXTAREA}
                      rows={3}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Ghi chú lễ tân"
                    />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditing(false)}
                    >
                      Hủy
                    </Button>
                    <Button
                      type="button"
                      onClick={() => edit.mutate()}
                      disabled={
                        !editDate ||
                        !editTime ||
                        editDuration < 1 ||
                        edit.isPending
                      }
                    >
                      {edit.isPending ? "Đang lưu…" : "Lưu thay đổi"}
                    </Button>
                  </div>
                  {edit.isError && (
                    // A taken slot is information, not a red error — same as the create page
                    // (#18787); #18681 measured hundreds of these in one data-entry session.
                    <div
                      className={
                        isSlotConflictError(edit.error)
                          ? "mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                          : "mt-2 text-sm text-destructive"
                      }
                      role={isSlotConflictError(edit.error) ? "status" : "alert"}
                    >
                      <p>{apiError(edit.error).detail}</p>
                      {/* Offer the doctor's next free starts rather than making the user
                          guess another time and press again (#18672). */}
                      <SlotConflictHint
                        error={edit.error}
                        onPick={(iso) => {
                          const slot = splitSlot(iso);
                          if (!slot) return;
                          setEditDate(slot.date);
                          setEditTime(slot.time);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {a.status === "CHECKED_IN" && (rooms.data ?? []).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Gọi vào phòng</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className={`${NATIVE_CONTROL} w-auto min-w-[12rem]`}
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                  >
                    <option value="">— Chọn phòng —</option>
                    {(rooms.data ?? []).map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.code} · {r.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    disabled={!roomId || callRoom.isPending}
                    onClick={() => callRoom.mutate()}
                  >
                    {callRoom.isPending ? "Đang gọi…" : "Gọi vào phòng"}
                  </Button>
                </div>
                {roomMsg && (
                  <p className="mb-0 mt-2 text-sm text-emerald-600">{roomMsg}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Thông tin đặt lịch: bệnh nhân (+ ghi chú lễ tân) ───────────────────────── */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle>Thông tin đặt lịch</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to={`/patients/${a.patientId}`}>
                  Mở hồ sơ
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <h3 className="mb-3 mt-0 text-sm font-semibold text-foreground">Thông tin bệnh nhân</h3>
              <div className="flex flex-col gap-4 sm:flex-row">
                <PatientAvatar
                  fullName={a.patientName}
                  zaloAvatarUrl={p?.zaloAvatarUrl}
                  avatarUrl={p?.avatarUrl}
                  className="size-14 text-lg"
                />
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  <IconRow icon={User} label="Họ tên">
                    <PatientLink
                      id={a.patientId}
                      name={a.patientName}
                      className="font-semibold text-primary"
                    />
                  </IconRow>
                  <IconRow icon={MapPin} label="Địa chỉ">
                    {address || (patient.isLoading ? "…" : "Chưa có thông tin")}
                  </IconRow>
                  <IconRow icon={Phone} label="Số điện thoại">
                    {phone ? (
                      <a href={`tel:${phone}`} className="font-semibold text-primary hover:underline">
                        {formatPhone(phone)}
                      </a>
                    ) : (
                      "Chưa có thông tin"
                    )}
                  </IconRow>
                  <IconRow icon={Cake} label="Ngày sinh">
                    {dob ? (
                      <>
                        {formatDate(dob)}
                        {p?.age != null && (
                          <span className="text-muted-foreground"> · {p.age} tuổi</span>
                        )}
                      </>
                    ) : (
                      "Chưa có thông tin"
                    )}
                  </IconRow>
                  <IconRow icon={VenusAndMars} label="Giới tính">
                    {p?.gender ? genderLabel[p.gender] : patient.isLoading ? "…" : "Chưa có thông tin"}
                  </IconRow>
                  <IconRow icon={Mail} label="Email">
                    {p?.email ? (
                      <a href={`mailto:${p.email}`} className="font-semibold text-primary hover:underline">
                        {p.email}
                      </a>
                    ) : patient.isLoading ? (
                      "…"
                    ) : (
                      "Chưa có thông tin"
                    )}
                  </IconRow>
                  <IconRow icon={IdCard} label="Mã bệnh nhân">
                    {a.patientCode ? (
                      <span className="font-mono">{a.patientCode}</span>
                    ) : (
                      "Chưa có mã"
                    )}
                  </IconRow>
                </div>
              </div>

              {a.notes && (
                <>
                  <Separator className="my-4" />
                  <h3 className="mb-2 mt-0 text-sm font-semibold text-foreground">Ghi chú lễ tân</h3>
                  <p className="m-0 whitespace-pre-wrap text-sm">{a.notes}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* ── Dịch vụ & Thanh toán ──────────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle>Dịch vụ &amp; Thanh toán</CardTitle>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                // Lịch chưa chọn dịch vụ nào là một trạng thái THẬT (lượt chuyển khoa, #18930).
                <p className="m-0 text-sm text-muted-foreground">Chưa chọn dịch vụ nào.</p>
              ) : (
                <>
                  <ul data-testid="appointment-services" className="m-0 list-none divide-y divide-border p-0">
                    {rows.map((r) => (
                      <li
                        key={r.key}
                        className="flex items-center justify-between gap-4 py-2 text-sm first:pt-0"
                      >
                        <span data-testid="appointment-service-name" className="min-w-0">
                          {r.name}
                        </span>
                        <span className="shrink-0 tabular-nums">
                          {r.legacy ? (
                            <span className="text-muted-foreground">—</span>
                          ) : r.shownVnd == null ? (
                            <span className="text-muted-foreground">Liên hệ</span>
                          ) : (
                            formatVndWithUnit(r.shownVnd)
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {pricedRows.length > 0 && (
                    <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
                      <span data-testid="appointment-services-total-label">
                        {invoiceOfTotal ? (
                          <>
                            Tổng cộng (theo hoá đơn{" "}
                            <Link to={`/invoices/${invoiceOfTotal.invoiceId}`} className="underline">
                              {invoiceOfTotal.invoiceNumber}
                            </Link>
                            )
                          </>
                        ) : (
                          "Tổng cộng (giá niêm yết)"
                        )}
                      </span>
                      <span className="tabular-nums" data-testid="appointment-services-total">
                        {formatVndWithUnit(total)}
                      </span>
                    </div>
                  )}
                  {hasUnpriced && pricedRows.length > 0 && (
                    <p className="mb-0 mt-1 text-xs text-muted-foreground">
                      Chưa gồm dịch vụ chưa niêm yết giá.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <ExamFeeCard
            appointmentId={a.id}
            patientName={a.patientName}
            patientCode={a.patientCode ?? null}
            collectorName={me.data?.fullName?.trim() || me.data?.username || "—"}
            serviceName={a.serviceName}
          />

          {/* ── Kết quả & Tài liệu (SalePro) ──────────────────────────────────────────── */}
          {hasExam &&
            (encounters.isLoading ? (
              <Card>
                <CardHeader>
                  <CardTitle>Kết quả &amp; Tài liệu</CardTitle>
                </CardHeader>
                <CardContent>
                  <Skeleton height={72} />
                </CardContent>
              </Card>
            ) : (
              // Không có lần khám HIS vẫn dựng thẻ: lượt khám ghi nhận từ phiếu QR chỉ có phiếu ngoài.
              <EncounterResultCard appointmentId={a.id} summary={encounters.data ?? null} />
            ))}

          <Card>
            <CardHeader>
              <CardTitle>Lịch sử thay đổi</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="m-0 list-none p-0">
                {a.events.length === 0 && (
                  <p className="m-0 text-sm text-muted-foreground">—</p>
                )}
                {a.events.map((ev, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 border-b border-border py-3 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-foreground">
                        {actionLabel(ev.action, ev.toStatus)}
                        {ev.toStatus && (
                          <AppointmentStatusBadge status={ev.toStatus} />
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {formatDateTime(ev.occurredAt)} · {ev.actor}
                        {ev.note && ` · ${ev.note}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Cột phải dính khi cuộn ở màn rộng; top-0 neo sát mép dưới của TopBar (bỏ lề p-6 của <main>) để cả cột lọt trong màn hình. */}
        <aside className="grid content-start gap-4 lg:sticky lg:top-0">
          {/* ── Mã đặt lịch · STT · phòng (SalePro: thẻ QR + Descriptions) ──────────── */}
          <Card>
            <CardContent className="pt-6">
              {a.bookingCode && (
                <div className="mb-4 flex flex-col items-center gap-2" data-testid="appointment-qr">
                  <div className="rounded-md border border-border bg-white p-2">
                    <QRCodeSVG value={a.bookingCode} size={96} level="M" aria-hidden="true" />
                  </div>
                  <span className="font-mono text-base font-semibold tracking-[0.2em] text-foreground">
                    {a.bookingCode}
                  </span>
                </div>
              )}
              <dl className="m-0 divide-y divide-border overflow-hidden rounded-md border border-border text-sm">
                {/*
                 * Mã đặt lịch (#19396) — đúng mã bệnh nhân thấy trên Zalo Mini App, để lễ tân đối
                 * chiếu bằng mắt khi bệnh nhân đọc nó; monospace vì đọc từng ký tự, `select-all`
                 * để copy bằng một lần bấm.
                 */}
                <DescRow label="Mã đặt lịch">
                  <span
                    data-testid="appointment-booking-code"
                    className="select-all font-mono tracking-wide"
                  >
                    {a.bookingCode ?? "—"}
                  </span>
                </DescRow>
                {/* #19934 — nói rõ lịch đặt VÀO ĐÂU và số nào là số nào: số vào phòng hay số quầy
                    tiếp đón (lễ tân xếp phòng sau thì bệnh nhân có cả hai). */}
                <DescRow label="Nơi tiếp nhận">
                  <span data-testid="appointment-destination">
                    {isCounterBooking(a) ? "Quầy tiếp đón" : "Vào thẳng phòng khám"}
                  </span>
                </DescRow>
                {sttRows(a).map((r, i) => (
                  <DescRow key={r.label} label={r.label}>
                    <span
                      data-testid={i === 0 ? "appointment-stt" : undefined}
                      className={i === 0 ? "text-base font-semibold" : undefined}
                    >
                      {r.value ?? "—"}
                    </span>
                  </DescRow>
                ))}
                <DescRow label="Phòng khám">{a.roomName ?? "—"}</DescRow>
                <DescRow label="Khoa">{a.departmentName ?? "—"}</DescRow>
                <DescRow label="Bác sĩ">{a.doctorName ?? "—"}</DescRow>
                <DescRow label="Ngày giờ khám">{formatDateTime(a.startAt)}</DescRow>
                <DescRow label="Thời lượng">{a.durationMinutes} phút</DescRow>
                <DescRow label="Tạo lúc" muted>
                  {formatDateTime(a.createdAt)}
                </DescRow>
                <DescRow label="Cập nhật" muted>
                  {formatDateTime(a.updatedAt)}
                </DescRow>
              </dl>
              <Button
                type="button"
                className="mt-4 w-full"
                onClick={() => setQrOpen(true)}
                disabled={!a.bookingCode}
                title={a.bookingCode ? undefined : "Lịch hẹn chưa có mã đặt lịch"}
              >
                <QrCode aria-hidden="true" />
                Mở mã QR
              </Button>
            </CardContent>
          </Card>

          {a.status === "COMPLETED" && (
            <Card>
              <CardHeader className="flex-row items-center gap-2 space-y-0">
                <CardTitle>Đánh giá sau khám</CardTitle>
              </CardHeader>
              <CardContent>
                {submitFeedback.isSuccess ? (
                  <p className="m-0 text-sm text-emerald-600">Đã ghi nhận đánh giá. Cảm ơn!</p>
                ) : (
                  <div className="grid gap-2">
                    <div className="flex items-center gap-1 text-2xl">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          aria-label={`${n} sao`}
                          className={n <= fbRating ? "text-amber-500" : "text-muted-foreground"}
                          onClick={() => setFbRating(n)}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                    <textarea
                      className="min-h-[60px] w-full rounded-md border border-input bg-background p-2 text-sm"
                      placeholder="Nhận xét (không bắt buộc)…"
                      maxLength={1000}
                      value={fbComment}
                      onChange={(e) => setFbComment(e.target.value)}
                    />
                    <div>
                      <Button
                        type="button"
                        size="sm"
                        disabled={fbRating < 1 || submitFeedback.isPending}
                        onClick={() => submitFeedback.mutate()}
                      >
                        {submitFeedback.isPending ? "Đang gửi…" : "Gửi đánh giá"}
                      </Button>
                      {submitFeedback.isError && (
                        <span className="ml-2 text-sm text-destructive">
                          {apiError(submitFeedback.error).detail}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {hasHis && (
            <Card>
              <CardHeader className="flex-row items-center gap-2 space-y-0">
                <Building2 aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
                <CardTitle>Thông tin HIS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2.5">
                  <Row label="Hệ thống HIS">{binding.data?.hisType ?? "—"}</Row>
                  <Row label="Phòng khám (HIS)">{a.roomName ?? "—"}</Row>
                  <Row label="STT (HIS)" emphasis>
                    {hisRow?.stt ?? "—"}
                  </Row>
                  <Row label="Mã đặt lịch HIS">{hisRow?.hisBookingId ?? "—"}</Row>
                  <Row label="Trạng thái đồng bộ" stack>
                    <HisSyncStatusBadge status={hisSyncStatus} action={hisRow?.action} />
                  </Row>
                  {hisRow && hisRow.status !== "CONFIRMED" && hisRow.attempts > 0 && (
                    <Row label="Số lần đã thử">{hisRow.attempts}</Row>
                  )}
                  {hisRow?.lastError && (
                    <Row label="Lỗi gần nhất" stack>
                      <span className="text-xs font-normal text-destructive">
                        {hisRow.lastError}
                      </span>
                    </Row>
                  )}
                  <Row label="Cập nhật lúc" muted>
                    {hisRow ? formatDateTime(hisRow.updatedAt) : "—"}
                  </Row>
                </div>

                {/* Manual push — MANUAL mode only, on a newly-created appointment not yet confirmed
                    on the HIS. In AUTO mode the scheduler handles delivery and this button is hidden. */}
                {canManualPush && hisSyncStatus !== "CONFIRMED" && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      data-testid="his-push-now"
                      disabled={pushHis.isPending}
                      onClick={() => pushHis.mutate()}
                    >
                      <Send aria-hidden="true" className="h-4 w-4" />
                      {pushHis.isPending ? "Đang gửi sang HIS…" : "Đặt lịch HIS ngay"}
                    </Button>
                    {pushHis.isError && (
                      <p className="mb-0 mt-2 text-sm text-destructive">
                        {apiError(pushHis.error).detail}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </aside>
      </div>

      {/* Mã QR phóng to (SalePro: nút "Mở mã QR" → modal). Bệnh nhân / lễ tân đối chiếu bằng mắt. */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mã QR đặt lịch</DialogTitle>
            <DialogDescription>
              Cùng mã bệnh nhân thấy trên Zalo Mini App, mục "Lịch sử khám".
            </DialogDescription>
          </DialogHeader>
          {a.bookingCode && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="rounded-md border border-border bg-white p-3">
                <QRCodeSVG value={a.bookingCode} size={224} level="M" aria-hidden="true" />
              </div>
              <p className="m-0 text-sm text-muted-foreground">
                Mã đặt lịch:{" "}
                <span className="select-all font-mono text-base font-semibold tracking-[0.2em] text-foreground">
                  {a.bookingCode}
                </span>
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** "Lịch sử thay đổi" row title (#19202) — the actions the appointments trigger writes. */
export function actionLabel(action: string, toStatus?: AppointmentStatus): string {
  switch (action) {
    case "CREATED":
    case "appointment.create":
      return "Tạo lịch hẹn";
    case "STATUS_CHANGED":
    case "appointment.transition":
      switch (toStatus) {
        case "CONFIRMED":
          return "Xác nhận lịch";
        case "CHECKED_IN":
          return "Tiếp đón (check-in)";
        case "CANCELLED":
          return "Huỷ lịch";
        case "NO_SHOW":
          return "Bệnh nhân không đến";
        case "COMPLETED":
          return "Hoàn thành khám";
        default:
          return "Đổi trạng thái";
      }
    case "RESCHEDULED":
    case "appointment.reschedule":
      return "Dời lịch";
    case "DOCTOR_ASSIGNED":
      return "Gán bác sĩ";
    case "ROOM_ASSIGNED":
      return "Xếp phòng / gọi vào phòng";
    case "REGISTERED":
      return "Cấp số tiếp đón";
    case "EDITED":
      return "Sửa lịch hẹn";
    default:
      return action;
  }
}

/** Dòng "icon · nhãn: giá trị" của khối Thông tin bệnh nhân — hình của SalePro (lucide + Text). */
function IconRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2 text-sm">
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="text-muted-foreground">{label}: </span>
        <span className="break-words text-foreground">{children}</span>
      </span>
    </div>
  );
}

/** Một dòng của bảng mô tả có viền (antd `Descriptions bordered` trên màn SalePro). */
function DescRow({
  label,
  children,
  muted = false,
}: {
  label: string;
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)]">
      <dt className="bg-muted/50 px-3 py-2 text-muted-foreground">{label}</dt>
      <dd
        className={
          muted
            ? "m-0 px-3 py-2 text-xs text-muted-foreground"
            : "m-0 px-3 py-2 font-medium text-foreground"
        }
      >
        {children}
      </dd>
    </div>
  );
}

function Row({
  label,
  children,
  emphasis = false,
  muted = false,
  stack = false,
}: {
  label: string;
  children: React.ReactNode;
  /** STT (HIS) — the field staff scan for first. */
  emphasis?: boolean;
  /** Secondary metadata (timestamps) — de-emphasized rather than competing for attention. */
  muted?: boolean;
  /** Label above value instead of side-by-side — for a value that can wrap to multiple lines
   * (sync-status badge, long error text): side-by-side + baseline alignment reads as visually
   * broken once both sides wrap independently at different points. */
  stack?: boolean;
}) {
  const valueClass = stack
    ? emphasis
      ? "text-base font-semibold text-foreground"
      : muted
        ? "text-xs text-muted-foreground"
        : "font-medium text-foreground"
    : emphasis
      ? "text-right text-base font-semibold text-foreground"
      : muted
        ? "text-right text-xs text-muted-foreground"
        : "text-right font-medium text-foreground";
  if (stack) {
    return (
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className={valueClass}>{children}</span>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={valueClass}>{children}</span>
    </div>
  );
}
